/**
 * TooltipPortal
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 *
 * Purpose:
 *   Presentational tooltip node for portaling to document.body. Renders the exact tooltip
 *   markup (position, styles, arrow) moved from BookingDetailModal. Caller is responsible for
 *   createPortal(..., document.body). When show is false, renders null.
 *
 * Assumptions:
 *   - All positioning and style values are passed as props (x, y, positionAbove, content, theme).
 *   - No hooks, effects, or API calls. Pure presentational only.
 *
 * Dependencies:
 *   - None (React only). Receives all values from parent.
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 6d: extracted from BookingDetailModal.jsx; tooltip markup and inline styles moved verbatim.
 */

function TooltipPortal({ show, content, x, y, positionAbove, theme, onMouseEnter, onMouseLeave }) {
  if (!show) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        // Transform based on position: above = translate(-50%, -100%), below = translate(-50%, 0)
        transform: positionAbove
          ? 'translate(-50%, -100%)'  // Above: center horizontally, align bottom to top of icon
          : 'translate(-50%, 0)',  // Below: center horizontally, align top to bottom of icon
        backgroundColor: theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(20, 20, 20, 0.95)',
        color: theme === 'dark' ? 'var(--color-text-primary-dark)' : '#ffffff',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 'var(--font-weight-normal)',
        lineHeight: '1.4',
        maxWidth: 'min(240px, calc(100vw - 40px))', // Ensure tooltip is visible within viewport (prevent clipping)
        zIndex: 10001, // Above modal (4001) to ensure visibility
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        pointerEvents: 'auto', // Allow clicking link inside (e.g. Channel Fees Info link)
        whiteSpace: 'normal',
        wordWrap: 'break-word',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {content}
      {/* Tooltip arrow - points toward icon */}
      <div style={{
        position: 'absolute',
        [positionAbove ? 'bottom' : 'top']: '-4px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        [positionAbove ? 'borderTop' : 'borderBottom']: `4px solid ${theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(20, 20, 20, 0.95)'}`,
      }} />
    </div>
  );
}

export default TooltipPortal;
